import React, { useState, useEffect } from "react";
import { Header1 } from "../comp/themes/modern/home/header/Header1";
import { ToastContainer, toast } from "react-toastify";
import { useBase } from "../../../store/useBase";
import { getPublicExhibitors } from "../../../services/exhibitor";
import { getAttendeeBookings, updateBooking } from "../../../services/meeting";
import { getFavorites, toggleFavorite } from "../../../services/favorites";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSite } from "../../../store/useSite";
import { Clock, Calendar, ChevronRight, User, Trash2, Loader2 } from "lucide-react";
import moment from "moment";
import { AvatarComp } from "../../ui/AvatarComp";
import Modal from "../../ui/Modal";

const ExhibitorGridSkeleton = () => (
  <div className="space-y-10">
    {[1, 2].map((cat) => (
      <div key={cat} className="space-y-4">
        <div className="h-6 w-44 bg-slate-200/80 rounded animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col items-center space-y-3 shadow-sm"
            >
              <div className="w-20 h-20 rounded-xl bg-slate-100 animate-pulse" />
              <div className="h-4 w-28 bg-slate-200/80 rounded animate-pulse" />
              <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const AppointmentsListSkeleton = () => (
  <div className="space-y-4 pt-2">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
      >
        <div className="col-span-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-slate-100 animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-4 w-40 bg-slate-200/80 rounded animate-pulse" />
            <div className="h-3 w-28 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
        <div className="col-span-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
          <div className="space-y-1.5">
            <div className="h-3.5 w-24 bg-slate-200/80 rounded animate-pulse" />
            <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
        <div className="col-span-3 flex justify-end">
          <div className="h-9 w-28 rounded-xl bg-slate-100 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

function SiteExhibitorsList() {
  const { eventId, siteSlug } = useBase();
  const { siteUser } = useSite();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState("all"); // "all" | "mylist" | "appointments"
  const [appointmentTab, setAppointmentTab] = useState("upcoming"); // "upcoming" | "completed"
  const [myList, setMyList] = useState(new Set()); // set of item ids
  const [exhibitorsData, setExhibitorsData] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const response = await getPublicExhibitors(eventId, debouncedSearch);
      if (response.data && response.data.data) {
        // Group by category_name
        const flatData = response.data.data.data || [];
        const grouped = flatData.reduce((acc, item) => {
          const catName = item.category_name || "Uncategorized";
          let category = acc.find((c) => c.category === catName);
          if (!category) {
            category = { category: catName, items: [] };
            acc.push(category);
          }
          category.items.push({
            id: item.id,
            name: item.name,
            image: item.logo || "https://via.placeholder.com/150?text=No+Logo",
          });
          return acc;
        }, []);
        setExhibitorsData(grouped);
      }
    } catch (error) {
      console.error("Error fetching exhibitors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [eventId, debouncedSearch]);

  const fetchAppointments = async () => {
    if (!siteUser?.id) return;
    setAppointmentsLoading(true);
    try {
      const response = await getAttendeeBookings(siteUser.id);
      if (response.data.success) {
        const data = response.data.data?.data || response.data.data || [];
        setAppointments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "appointments" && siteUser) {
      fetchAppointments();
    }
  }, [activeTab, siteUser?.id]);
  
  const fetchFavorites = async () => {
    if (!eventId || !siteUser) return;
    try {
      const response = await getFavorites(eventId);
      if (response.data.success) {
        const favoriteIds = new Set(response.data.data.map(item => item.id));
        setMyList(favoriteIds);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [eventId, siteUser?.id]);

  const toggleMyList = async (id) => {
    if (!siteUser) {
      toast.info("Please sign in to manage your list");
      return;
    }
    
    try {
      const response = await toggleFavorite(eventId, id);
      if (response.data.success) {
        setMyList((prev) => {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        });
        toast.success(response.data.data?.message || response.data.message);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorites");
    }
  };

  const handleCardClick = (id) => {
    navigate(`/${slug}/exhibitors/profile/${id}`);
  };

  const handleCancelMeeting = async (e, bookingId) => {
    e.stopPropagation();
    setSelectedBookingId(bookingId);
    setIsCancelModalOpen(true);
  };

  const performCancelMeeting = async (bookingId) => {
    setIsCancelModalOpen(false);
    setCancellingId(bookingId);
    try {
      const response = await updateBooking(bookingId, { status: "cancelled" });
      if (response.data.success) {
        toast.success("Meeting cancelled successfully");
        fetchAppointments();
      }
    } catch (error) {
      console.error("Error cancelling meeting:", error);
      toast.error("Failed to cancel meeting");
    } finally {
      setCancellingId(null);
      setSelectedBookingId(null);
    }
  };

  // For "My List" tab: filter exhibitorsData to only bookmarked items
  const myListData = exhibitorsData
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => myList.has(item.id)),
    }))
    .filter((cat) => cat.items.length > 0);

  const displayData = activeTab === "all" ? exhibitorsData : myListData;

  const tabClass = (tab) =>
    tab === activeTab
      ? "px-4 sm:px-6 py-4 text-[#ea580c] border-b-2 border-[#ea580c] font-medium text-sm whitespace-nowrap"
      : "px-4 sm:px-6 py-4 text-gray-500 hover:text-gray-700 border-l text-sm whitespace-nowrap";

  return (
    <>
      <Header1 />
      {/* Main Content */}
      {/* Hero */}
      <main className="flex flex-col items-center justify-center text-black text-center px-4 h-[220px] sm:h-[300px] bg-cover bg-center <div  bg-gradient-to-br from-blue-200 to-purple-300">
        <h1
          className="text-2xl sm:text-4xl md:text-5xl tracking-wider mb-2 font-bold
          "
        >
          EXHIBITORS
        </h1>
        <p className="text-xl sm:text-sm md:text-base mb-5 text-gray-700 sm:mb-8">
          Explore the companies you can meet face-to-face
        </p>
        <Link
          to={`/${siteSlug}/exhibitors/category`}
          className="uppercase text-white
    text-sm md:text-base
    font-medium
    px-4 md:px-8 py-3
    rounded-lg
    bg-gradient-to-r from-purple-500 to-indigo-500
    hover:from-purple-600 hover:to-indigo-600
    transition-all duration-200
    shadow-md
    border border-purple-400
    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
  "
        >
          Apply to Exhibit
        </Link>
      </main>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="max-w-6xl mx-auto flex overflow-x-auto">
          <button
            className={tabClass("all")}
            onClick={() => setActiveTab("all")}
          >
            All Exhibitors
          </button>
          <button
            className={tabClass("appointments")}
            onClick={() => setActiveTab("appointments")}
          >
            My Appointments
          </button>
          <button
            className={tabClass("mylist")}
            onClick={() => setActiveTab("mylist")}
          >
            My List
            {myList.size > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#ea580c] text-white text-[10px] font-bold">
                {myList.size}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto mt-4 sm:mt-8 mb-20 bg-white border border-gray-200 rounded shadow-sm">
        <section className="p-4 sm:p-8 min-h-[400px]">
          {/* Search — only on All Exhibitors tab */}
          {activeTab === "all" && (
            <div className="mb-8">
              <div className="relative w-full sm:max-w-xs">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#f97316] focus:border-[#f97316]"
                />
                {loading && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg
                      className="animate-spin h-4 w-4 text-[#ea580c]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State for initial load */}
          {loading && exhibitorsData.length === 0 ? (
            <ExhibitorGridSkeleton />
          ) : (
            <>
              {/* Categories */}
              {activeTab === "appointments" ? (
                <div className="space-y-6">
                  {/* Appointment Sub-Tabs */}
                  <div className="flex items-center gap-4 border-b border-gray-100 pb-2">
                    <button
                      onClick={() => setAppointmentTab("upcoming")}
                      className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${
                        appointmentTab === "upcoming"
                          ? "bg-[#E64D4D] text-white shadow-sm"
                          : "bg-white text-gray-500 border border-gray-100"
                      }`}
                    >
                      Upcoming
                    </button>
                    <button
                      onClick={() => setAppointmentTab("completed")}
                      className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${
                        appointmentTab === "completed"
                          ? "bg-[#E64D4D] text-white shadow-sm"
                          : "bg-white text-gray-500 border border-gray-100"
                      }`}
                    >
                      Completed
                    </button>
                  </div>

                  {appointmentsLoading ? (
                    <AppointmentsListSkeleton />
                  ) : (
                    <div className="space-y-4 pt-2">
                      {appointments
                        .filter((b) => {
                          if (appointmentTab === "upcoming") {
                            return (
                              b.status === "pending" || b.status === "confirmed"
                            );
                          } else {
                            return (
                              b.status === "completed" ||
                              b.status === "cancelled"
                            );
                          }
                        })
                        .map((booking) => (
                          <div
                            key={booking.id}
                            className="bg-white border border-slate-200 rounded-3xl p-4 sm:p-6 flex flex-col md:grid md:grid-cols-12 gap-6 items-center hover:border-red-100 hover:shadow-sm transition-all"
                          >
                            {/* Company & Time Section */}
                            <div className="col-span-6 flex items-center gap-4 w-full">
                              <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {booking.exhibitor_logo ? (
                                  <img
                                    src={booking.exhibitor_logo}
                                    alt={booking.exhibitor_name}
                                    className="max-w-full max-h-full object-contain p-2"
                                  />
                                ) : (
                                  <Calendar className="text-slate-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-bold text-gray-900 truncate">
                                  {booking.exhibitor_name}
                                </h3>
                                <p className="text-sm text-gray-500 font-bold mt-1 flex items-center gap-1.5">
                                  {moment(booking.start_datetime).format("ddd, MMM DD, YYYY")}
                                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                  {moment(booking.start_datetime).format("hh:mm A")}
                                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                  <span className="text-indigo-400">
                                    {booking.slot_duration || '-'} Minutes
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Host Section */}
                            <div className="col-span-3 flex items-center gap-3 w-full border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-6">
                              <AvatarComp
                                name={`${booking.member_first_name} ${booking.member_last_name || ""}`}
                                size="md"
                              />
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-gray-900">
                                  {booking.member_first_name}{" "}
                                  {booking.member_last_name || ""}
                                </span>
                                <span className="text-xs text-gray-400 font-bold">
                                  Booth Member
                                </span>
                              </div>
                            </div>

                            {/* Action Section */}
                            <div className="col-span-3 flex justify-end w-full border-t md:border-t-0 border-slate-100 pt-4 md:pt-0">
                              {appointmentTab === "upcoming" &&
                                booking.status !== "cancelled" && (
                                  <button
                                    onClick={(e) =>
                                      handleCancelMeeting(e, booking.id)
                                    }
                                    disabled={cancellingId === booking.id}
                                    className="w-full md:w-auto px-6 py-2.5 bg-red-50 text-red-500 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-red-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                  >
                                    {cancellingId === booking.id ? (
                                      <>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        CANCELLING...
                                      </>
                                    ) : (
                                      "CANCEL MEETING"
                                    )}
                                  </button>
                                )}
                              {booking.status === "cancelled" && (
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
                                  Cancelled
                                </span>
                              )}
                              {booking.status === "completed" && (
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500 bg-emerald-50 px-4 py-2 rounded-full border border-emerald-100">
                                  Completed
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      {appointments.filter((b) => {
                        if (appointmentTab === "upcoming")
                          return (
                            b.status === "pending" || b.status === "confirmed"
                          );
                        return (
                          b.status === "completed" || b.status === "cancelled"
                        );
                      }).length === 0 && (
                        <div className="text-center py-10 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                          <p className="text-gray-400 font-bold text-sm">
                            No {appointmentTab} appointments found
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : activeTab === "mylist" && myListData.length === 0 ? (
                <EmptyMyList />
              ) : displayData.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-500">
                    No exhibitors found matching your criteria.
                  </p>
                </div>
              ) : (
                displayData.map((cat, index) => (
                  <CategorySection
                    key={index}
                    {...cat}
                    myList={myList}
                    onToggleMyList={toggleMyList}
                    onCardClick={handleCardClick}
                  />
                ))
              )}
            </>
          )}
        </section>
      </main>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setSelectedBookingId(null);
        }}
        title="Cancel Meeting"
        maxWidth="max-w-md"
        footer={
          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsCancelModalOpen(false);
                setSelectedBookingId(null);
              }}
              className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              No, keep it
            </button>
            <button
              onClick={() => performCancelMeeting(selectedBookingId)}
              className="px-5 py-2 text-xs font-black uppercase tracking-widest bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-sm"
            >
              Yes, cancel
            </button>
          </div>
        }
      >
        <div className="py-6">
          <p className="text-gray-600 font-medium">
            Are you sure you want to cancel this meeting? This action cannot be
            undone.
          </p>
        </div>
      </Modal>
    </>
  );
}

const ExhibitorCard = ({
  id,
  name,
  image,
  isBookmarked,
  onToggle,
  onClick,
}) => (
  <div
    onClick={() => onClick(id)}
    className="border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300 cursor-pointer relative
    flex flex-row items-center gap-4 p-3
    sm:flex-col sm:items-center sm:justify-center sm:p-6 sm:aspect-square group"
  >
    {/* Bookmark button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle(id);
      }}
      className={`absolute top-2 right-2 transition-all duration-300 opacity-0 group-hover:opacity-100 group-hover:text-gray-600`}
      title={isBookmarked ? "Remove from My List" : "Add to My List"}
    >
      {isBookmarked ? (
        // Filled bookmark
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="#ea580c"
          className="w-4 h-4"
        >
          <path
            fillRule="evenodd"
            d="M6.32 2.577a49.255 49.255 0 0111.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 01-1.085.67L12 18.089l-7.165 3.583A.75.75 0 013.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        // Outline bookmark
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"
          />
        </svg>
      )}
    </button>

    <div
      className="h-14 w-14 flex-shrink-0 flex items-center justify-center
      sm:h-24 sm:w-24 sm:mb-4"
    >
      <img
        src={image}
        alt={name}
        className="max-h-full max-w-full object-contain"
      />
    </div>
    <span className="text-sm font-semibold text-gray-900 transition-all duration-300">
      {name}
    </span>
  </div>
);

const CategorySection = ({
  category,
  items,
  myList,
  onToggleMyList,
  onCardClick,
}) => (
  <div className="mb-10 transition-all duration-300">
    <h2 className="text-lg font-semibold text-gray-800 mb-4 transition-all duration-300">
      {category}
    </h2>

    {/* Mobile: single-column list | sm+: multi-column grid */}
    <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:gap-4 transition-all duration-300">
      {items.map((item, index) => (
        <ExhibitorCard
          key={index}
          {...item}
          isBookmarked={myList.has(item.id)}
          onToggle={onToggleMyList}
          onClick={onCardClick}
        />
      ))}
    </div>
  </div>
);

const EmptyMyList = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center transition-all duration-300">
    {/* Stall icon */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-24 h-24 text-gray-300 mb-6"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
      />
    </svg>
    <p className="text-gray-700 font-semibold text-base transition-all duration-300">
      There are no exhibitors on your list
    </p>
    <p className="text-gray-400 text-sm mt-2 transition-all duration-300">
      Browse <strong>All Exhibitors</strong> and tap the bookmark icon to save
      them here.
    </p>
  </div>
);

const EmptyAppointments = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center transition-all duration-300">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-24 h-24 text-gray-300 mb-6"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
      />
    </svg>
    <p className="text-gray-700 font-semibold text-base transition-all duration-300">
      You have no appointments scheduled
    </p>
    <p className="text-gray-400 text-sm mt-2 transition-all duration-300">
      Your scheduled meetings with exhibitors will appear here.
    </p>
  </div>
);

export default SiteExhibitorsList;
